import { type RefObject, useLayoutEffect, useRef, useState } from "react";

export type CannabinoidLabelVariant = "full" | "short";

export function computeCannabinoidLabelVariant(
	rowArea: HTMLElement,
	fullMeasureRow: HTMLElement,
): CannabinoidLabelVariant {
	const availableWidth = rowArea.clientWidth;
	if (availableWidth <= 0) {
		return "full";
	}
	if (fullMeasureRow.scrollWidth <= availableWidth) {
		return "full";
	}
	return "short";
}

type CannabinoidLabelPair = {
	fullLabel: string;
	shortLabel: string;
};

type AdaptiveCannabinoidLabel = {
	label: string;
	title: string;
};

/**
 * Picks full labels for both badges when they fit side by side in the row;
 * otherwise short for both. The decision is based on the row width and a
 * hidden measurement row that always renders the full labels, so the result
 * is independent of the currently displayed variant and recovers to full
 * when the row grows again.
 */
export function useAdaptiveCannabinoidLabelPair(
	thc: CannabinoidLabelPair,
	cbd: CannabinoidLabelPair,
): {
	rowRef: RefObject<HTMLDivElement | null>;
	fullMeasureRowRef: RefObject<HTMLDivElement | null>;
	thc: AdaptiveCannabinoidLabel;
	cbd: AdaptiveCannabinoidLabel;
	variant: CannabinoidLabelVariant;
} {
	const rowRef = useRef<HTMLDivElement>(null);
	const fullMeasureRowRef = useRef<HTMLDivElement>(null);
	const [variant, setVariant] = useState<CannabinoidLabelVariant>("full");
	const measureKey = `${thc.fullLabel}|${thc.shortLabel}|${cbd.fullLabel}|${cbd.shortLabel}`;

	useLayoutEffect(() => {
		if (measureKey.length === 0) {
			return;
		}
		const rowArea = rowRef.current;
		const fullMeasureRow = fullMeasureRowRef.current;
		if (!rowArea || !fullMeasureRow) {
			return;
		}
		const updateVariant = (): void => {
			const currentRowArea = rowRef.current;
			const currentFullMeasureRow = fullMeasureRowRef.current;
			if (!currentRowArea || !currentFullMeasureRow) {
				return;
			}
			setVariant(
				computeCannabinoidLabelVariant(currentRowArea, currentFullMeasureRow),
			);
		};
		updateVariant();
		const observer = new ResizeObserver(updateVariant);
		observer.observe(rowArea);
		observer.observe(fullMeasureRow);
		return () => observer.disconnect();
	}, [measureKey]);

	return {
		rowRef,
		fullMeasureRowRef,
		thc: {
			label: variant === "full" ? thc.fullLabel : thc.shortLabel,
			title: thc.fullLabel,
		},
		cbd: {
			label: variant === "full" ? cbd.fullLabel : cbd.shortLabel,
			title: cbd.fullLabel,
		},
		variant,
	};
}
